import { HTMLTable } from "./HTMLTable";
import { IHeaderCol, ISortByCol, ICoreFrameGridOptions, IStylingInformation } from "../Interfaces";
import { CoreFrameGrid } from "../index";
import { Constants } from "../Constants";
import { SortState } from "../Components/EventHelper";
export class HeaderTable extends HTMLTable {

    /**
     * Der Tabellenkopf
     */
    $thead: JQuery<HTMLElement> = undefined;
    
    /**
     * Enthält die Zeilen der Tabellenspalten
     */
    theadRows: JQuery<HTMLElement>[] = undefined;
    
    /**
     * Speichert die "Zeilen" der Spalten
     * ### Spalte 1, Spalte 2, Spalte 3 ###
     * ### Unterspalte 1, Unterspalte 2 ###
     * ### UnterUnterspalte 1           ###
     */
    headerRows: IHeaderCol[][];

    /**
     * Id der Header-Rowgroup. Wird benötigt, damit die Body-Tabelle (das eigentliche role="grid")
     * die Spaltenköpfe per aria-owns als Teil desselben Grids ausweisen kann.
     */
    private readonly headerRowGroupId: string;

    constructor(options: ICoreFrameGridOptions, grid: CoreFrameGrid) {
        super('GridHeaderTable', options, grid);

        // Da diese Tabelle nur die Spaltenköpfe enthält, role="presentation"
        this.$table.attr('role', 'presentation');

        this.headerRowGroupId = 'cf-grid-header-rowgroup-' + this.getIdentifier();
        this.$thead = $('<thead role="rowgroup">').attr('id', this.headerRowGroupId);
        this.theadRows = [];

        this.$thead.appendTo(this.$table);

        this.$wrapper[0].style.overflow = 'hidden';
        this.$wrapper[0].style.position = 'relative';
    }

    /**
     * Id der Header-Rowgroup für die aria-owns-Verknüpfung am Body-Grid.
     */
    public getHeaderRowGroupId(): string {
        return this.headerRowGroupId;
    }

    /**
     * Gibt den aktuellen Sortierausdruck des Grids zurück,
     * indem die Attribute aus dem DOM ausgelesen werden
     */
    public getSortExpression(): ISortByCol[] {
        let ret: ISortByCol[] = [];
        this.theadRows.forEach(row => {
            row.find('[data-' + this.options.sortDataName + ']').each((i, element) => {
                let $el = $(element);
                let sort = $el.attr('data-' + this.options.sortDataName) as any as SortState;
                let value = $el.data('sortfield');
                let index = $el.data('sortindex');
                ret.push({
                    dir: sort,
                    field: value,
                    index: index
                });
            })
        });

        ret = ret.sort((a, b) => {
            if (a.index < b.index)
                return -1;
            if (a.index > b.index)
                return 1;
            if (a.index == b.index)
                return 0;
        });
        return ret;
    }

    public getStylingInformation(): IStylingInformation {
        const headerColumnElem = this.$thead.find('th')[0];
        // should be present at any time - data gets loaded async, but columns are present directly on grid load
        if (headerColumnElem) {
            const computedStyle = window.getComputedStyle(headerColumnElem)
            return {
                fontFamily: computedStyle.fontFamily,
                fontSize: computedStyle.fontSize,
                fontWeight: computedStyle.fontWeight
            };
        }

        // this path should not be returned at any time, because the grid-header is present at any time. If there is a fault and the
        // headerColumnElem is not accessible, we return some "default"-values to make sure that any kind of calculation can be done in process later
        // the "default"-values will be provided by the <body> Element to make sure, that every calculation is based on an existing style and is mapping to the page-style
        const bodyElem = document.getElementsByTagName('body')[0];
        const computedStyleBody = window.getComputedStyle(bodyElem)
        return {
            fontFamily: computedStyleBody.fontFamily,
            fontSize: computedStyleBody.fontSize,
            fontWeight: computedStyleBody.fontWeight
        };
    }

    /**
     * Setzt die Attribute im DOM, um die Sortierung zu visualisieren
     * @param sort
     */
    public setSortExpression(sort: ISortByCol[]){
        // In jedem Fall Sortierung entfernen
        let $allTh = this.$thead.find('th');
        $allTh.removeAttr('data-' + this.options.sortDataName);

        if(sort == null || sort.length == 0)
            return;
        
        sort.forEach((currentSortExpression, sortIndex) => {
            $allTh.each((columnIndex, element) => {
                let $elem = $(element);
                if($elem.data('sortfield') == currentSortExpression.field){
                    $elem.attr('data-' + this.options.sortDataName, currentSortExpression.dir);
                    $elem.data('sortindex', sortIndex);
                }
            });
        });
    }

    public isSortedBy(col: IHeaderCol): boolean{
        const sortExpression = this.getSortExpression();

        return sortExpression.filter(s => s.field == col.field).length > 0;
    }

    // TODO: ENTFERNEN
    /**
     * obsolete
     * @param ergname
     */
    private ResolveErgName(ergname: string) {
        if (!ergname)
            ergname = '';
        if (this.options.getResource) {
            return this.options.getResource(ergname);
        }
        return ergname;
    }

    /**
     * Liefert die "Zeilen" der Spalten
     * ### Spalte 1, Spalte 2, Spalte 3 ###
     * ### Unterspalte 1, Unterspalte 2 ###
     * ### UnterUnterspalte 1           ###
     */
    public getHeaderRows(): IHeaderCol[][]{
        if(this.headerRows !== undefined)
            return this.headerRows;

        const visibleColumns = this.grid.getAllVisibleRootColumns();
        let result = [visibleColumns];
        let childRow = this.getChildHeaderRows(visibleColumns);
        result.splice(1, 0, ...childRow);

        this.headerRows = result;

        return result;
    }

    /**
     * Interne Hilfsmethode, um untergeordnete Spalten zu finden
     * @param cols 
     */
    private getChildHeaderRows(cols: IHeaderCol[]): IHeaderCol[][] {    
        let nextRow = [];
        cols.forEach(c => {
            if(c.subColumns)
                nextRow.push(...c.subColumns);
        });

        if(nextRow.length == 0)
            return nextRow;
        else{
            let result = [ nextRow ]
            let childRows = this.getChildHeaderRows(nextRow);
            if(childRows.length > 0)
                result = result.concat(childRows);
            
            return result;
        }
            
    }

    /**
     * Gibt die Koordinaten (Zeile, Spalte) einer Spalte zurück
     * @param col 
     */
    public getHeaderRowColIndexAndRowIndex(col: IHeaderCol): { row: number, col: number } {
        const headerRows = this.getHeaderRows();

        for (let x = 0; x < headerRows.length; x++) {
            const currentRow = headerRows[x];
            for (let y = 0; y < currentRow.length; y++) {
                if (currentRow[y] === col) {
                    return { row: x, col: y };
                }
            }
        }

        return { row: NaN, col: NaN };
    }

    /**
     * Gibt die Spalte anhand ihrer Koordinaten zurück
     * (z.B. 0,1 -> 1. Zeile, 2. Spalte)
     * @param row 
     * @param col 
     */
    public getHeaderColByColIndexAndRowIndex(row: number, col: number): IHeaderCol {
        return this.getHeaderRows()[row][col];
    }

    /**
     * Gibt die Spalte zurück, die zur Id passt (Format: "rowindex-colindex")
     * @param id
     */
    public getHeaderColById(id: string): IHeaderCol {
        let coordinates = id.split('-');

        return this.getHeaderColByColIndexAndRowIndex(parseInt(coordinates[0]), parseInt(coordinates[1]));
    }

    /**
     * Füllt den Header mit neuen Table-Headern
     */
    public fillHeader() {
        let _newResizer = '';
        this.headerRows = undefined;
        let headerRows = this.getHeaderRows();

        // Vorher speichern
        const sortExpression = this.getSortExpression();

        // Vorherige Header-Zeilen löschen
        this.$thead.empty();
        this.theadRows = [];

        headerRows.forEach((rowCols: IHeaderCol[], rowIndex) => {
            const $theadRow = $('<tr role="row"></tr>')
            let _ths = [];
            rowCols.forEach((col, index) => {
                let title = (col.ergName ? this.ResolveErgName(col.ergName) : col.title);
                let filteredTitle = (title ? title : '');
                let $th = $('<th role="columnheader"></th>').text(filteredTitle);

                if (this.grid.useLocator())
                    $th.attr('data-locator', this.options.locator + '.header.[' + (col.name ? col.name : (col.field ? col.field : filteredTitle)) + ']');
                if (col.field)
                    $th.attr('data-field', col.field);
                $th.attr('data-title', filteredTitle);
                $th.attr('data-index', `${rowIndex}-${index}`);
                const columnHasSubColumns = this.grid.columnHasSubColumns(col);

                if (headerRows.length > 0) {
                    // Colspan
                    if (columnHasSubColumns) {
                        $th.attr('colspan', col.subColumns.length);
                    }
                    // Rowspan
                    if (!columnHasSubColumns) {
                        $th.attr('rowspan', headerRows.length - rowIndex);
                    }

                    if(columnHasSubColumns)
                        $th.attr('data-subcolumns', col.subColumns.length)
                }

                if (this.options.isSortable !== false && col.isSortable) {
                    $th.addClass('sortable');
                }
                $th.data('sortfield', col.sortValue);

                _ths.push($th);
                
                // Resizer werden vorerst nur für Spalten eingefügt, die keine Unterspalten besitzen
                if(!columnHasSubColumns && this.isColumnResizable(col))
                    _newResizer += '<div class="' + Constants.CLASS_RESIZER + '" data-id="' + `${rowIndex}-${index}` + '"  data-columnname="' + col.name + '"></div>';
            });

            if(rowIndex == 0){
                // Auffüllende Spalte
                const $fillColumn = $('<th>').attr('aria-hidden', 'true');
                // Damit beim vertikale Scrollen die Spalten übereinander bleiben
                const $scrollColumn = $('<th>').attr('aria-hidden', 'true');

                if(headerRows.length > 0){
                    $fillColumn.attr('rowspan', headerRows.length);
                    $scrollColumn.attr('rowspan', headerRows.length);
                }

                _ths.push($fillColumn);
                _ths.push($scrollColumn);
            }

            $theadRow.append(_ths);
            this.theadRows.push($theadRow);
        });        

        this.$wrapper.children('.' + Constants.CLASS_RESIZER).remove();
        this.$wrapper.append(_newResizer);
        this.$thead.append(this.theadRows);

        this.setSortExpression(sortExpression);
    }

    /**
     * Gibt an, ob der Header (Spalten) sichtbar ist
     */
    public isHeaderVisible(): boolean{
        return this.options.showHeader;
    }

    public isColumnResizable(col: IHeaderCol){
        return col.isResizable !== false;
    }

    /**
     * Gibt alle Resizer-Elemente zurück
     */
    public getAllResizer(): JQuery<HTMLDivElement> {
        return this.$wrapper.find('div.' + Constants.CLASS_RESIZER) as JQuery<HTMLDivElement>;
    }
}
